/**
 * Rock History
 *
 * Displays version history / audit trail for a rock.
 * Loads history on-demand when expanded.
 */

import { useMemo, useState, useEffect } from "react";
import { ChevronDown, ChevronRight, History, User, Loader2, Trash2 } from "lucide-react";
import { compareDesc, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { cn, formatRelativeTime } from "@/lib/utils";
import { useFetchRockHistory, useDeleteHistoryItem } from "@/hooks/use-rocks-mutations";
import { isTerminalHealth, type WeeklySnapshot } from "@/types/rocks";

interface RockChange {
  field: string;
  old_value: unknown;
  new_value: unknown;
  description?: string;  // For milestone/deliverable changes
}

interface RockVersion {
  version: number;
  timestamp: string;
  user_id: string;
  user_name: string;
  change_type: string;
  changes: RockChange[];
}

/** Either a real version row or a synthetic entry derived from a weekly note. */
type AuditEntry =
  | { kind: "version"; key: string; timestamp: string; user_name: string; version: RockVersion }
  | {
      kind: "weekly_note";
      key: string;
      timestamp: string;
      user_name: string;
      health: WeeklySnapshot["health"];
      week_number: number;
      notes: string;
      isTerminal: boolean;
    };

interface RockHistoryProps {
  rockId: string;
  className?: string;
  rockVersion?: number;  // Increment this to trigger reload
  /** Weekly snapshot list — when provided, snapshot notes get merged into the
   * audit feed so weekly + closing notes show alongside version edits. */
  preloadedSnapshots?: WeeklySnapshot[];
}

// Format change type for display
const changeTypeLabels: Record<string, { label: string; color: string }> = {
  status_change: { label: "Status", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  milestone_complete: { label: "Milestone", color: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300" },
  deliverable_complete: { label: "Deliverable", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  update: { label: "Update", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
  blocker_add: { label: "Blocker", color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" },
  weekly_note: { label: "Note", color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  closing_note: { label: "Closing", color: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300" },
};

// Format a field name for display
function formatFieldName(field: string): string {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Format a value for display. Historically dumped objects as "[object Object]"
// — now handles the shapes we actually store (blockers, dependencies, measures,
// milestones, deliverables, raci) so history reads like a log, not gibberish.
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    // Status values
    if (["on_track", "off_track", "not_done"].includes(value)) {
      return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "none";
    // Array of strings (measures_of_success, …) — join with semicolons.
    if (value.every((v) => typeof v === "string")) return (value as string[]).join("; ");
    // Array of description-bearing records (blockers, dependencies, milestones, deliverables).
    if (value.every((v) => v && typeof v === "object" && "description" in (v as Record<string, unknown>))) {
      const parts = (value as Array<Record<string, unknown>>).map((v) => {
        const desc = String(v.description ?? "");
        if (typeof v.severity === "string") return `[${v.severity}] ${desc}`;
        return desc;
      });
      return parts.join("; ");
    }
    // Unknown array shape — fall back to count so it's at least readable.
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (typeof value === "object") {
    // RACI-like {responsible, accountable, consulted, informed}.
    const obj = value as Record<string, unknown>;
    const parts = Object.entries(obj)
      .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : !!v))
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? (v as unknown[]).join(", ") : String(v)}`);
    return parts.length ? parts.join("; ") : "none";
  }
  return String(value);
}

// Format a change for display (special handling for milestone/deliverable completions)
function formatChange(change: RockChange): { label: string; value: string } {
  const isMilestoneOrDeliverable =
    change.field.includes('milestones') || change.field.includes('deliverables');

  if (isMilestoneOrDeliverable && change.description) {
    // Show "completed: description" or "uncompleted: description"
    const action = change.new_value ? "completed" : "uncompleted";
    const truncatedDesc = change.description.length > 60
      ? change.description.substring(0, 60) + "..."
      : change.description;
    return {
      label: action,
      value: truncatedDesc
    };
  }

  // Default formatting for other fields
  return {
    label: formatFieldName(change.field),
    value: formatValue(change.new_value)
  };
}

export function RockHistory({ rockId, className, rockVersion, preloadedSnapshots }: RockHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [versions, setVersions] = useState<RockVersion[]>([]);
  const { mutate: fetchHistory, isPending } = useFetchRockHistory();
  const { mutate: deleteHistory, isPending: isDeleting } = useDeleteHistoryItem();

  const loadHistory = () => {
    fetchHistory(
      { rockId, limit: 50 },
      {
        onSuccess: (data) => {
          setVersions(data);
        },
      }
    );
  };

  const handleToggle = () => {
    if (!expanded) {
      // Fetch fresh history every time we expand
      loadHistory();
    }
    setExpanded(!expanded);
  };

  // Reload history when rock version changes (milestone/deliverable/status updates).
  // expanded and loadHistory intentionally omitted — handleToggle already loads on expand,
  // and loadHistory is unstable (not useCallback-wrapped).
  useEffect(() => {
    if (expanded && rockVersion !== undefined) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rockVersion]);

  const handleDelete = (versionNum: number) => {
    deleteHistory(
      { rockId, version: versionNum },
      {
        onSuccess: () => {
          // Remove from local state
          setVersions((prev) => prev.filter((v) => v.version !== versionNum));
        },
      }
    );
  };

  // Merge version history with weekly snapshot notes (regular + closing) into
  // a single chronological audit feed. Snapshots without saved_at are skipped
  // because we can't place them on the timeline.
  const entries: AuditEntry[] = useMemo(() => {
    const versionEntries: AuditEntry[] = versions.map((v, idx) => ({
      kind: "version",
      key: `v-${v.version}-${idx}`,
      timestamp: v.timestamp,
      user_name: v.user_name,
      version: v,
    }));
    const snapEntries: AuditEntry[] = (preloadedSnapshots ?? [])
      .filter((s) => s.notes && s.notes.trim().length > 0 && s.saved_at)
      .map((s) => ({
        kind: "weekly_note",
        key: `wk-${s.week_number}-${s.saved_at}`,
        timestamp: s.saved_at as string,
        user_name: s.saved_by ?? "—",
        health: s.health,
        week_number: s.week_number,
        notes: s.notes ?? "",
        isTerminal: isTerminalHealth(s.health),
      }));
    return [...versionEntries, ...snapEntries].sort((a, b) =>
      compareDesc(parseISO(a.timestamp), parseISO(b.timestamp)),
    );
  }, [versions, preloadedSnapshots]);

  const hasHistory = entries.length > 0;

  return (
    <div className={cn("rounded-lg border", className)}>
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-2.5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold text-xs">HISTORY</span>
          {/* Only show the count once expanded — `versions` is empty until the
              fetch resolves, so the badge would otherwise jump from N
              (snapshot-only) to N+M (snapshots + history) on first open. */}
          {hasHistory && expanded && (
            <Badge variant="secondary" className="text-[10px]">
              {entries.length}
            </Badge>
          )}
        </div>
        {(isPending || isDeleting) && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3">
          {isPending && (
            <div className="text-xs text-muted-foreground text-center py-4">
              Loading history...
            </div>
          )}

          {!isPending && !hasHistory && (
            <div className="text-xs text-muted-foreground text-center py-4">
              No changes recorded yet
            </div>
          )}

          {!isPending && hasHistory && (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {entries.map((entry) =>
                entry.kind === "version" ? (
                  <HistoryItem
                    key={entry.key}
                    version={entry.version}
                    onDelete={() => handleDelete(entry.version.version)}
                    isDeleting={isDeleting}
                  />
                ) : (
                  <NoteItem key={entry.key} entry={entry} />
                ),
              )}
            </div>
          )}

          {hasHistory && versions.length >= 50 && (
            <div className="text-xs text-muted-foreground text-center pt-2 border-t mt-2">
              Showing latest 50 versioned changes (weekly notes always included)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Individual history item
function HistoryItem({
  version,
  onDelete,
  isDeleting,
}: {
  version: RockVersion;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const typeConfig = changeTypeLabels[version.change_type] || {
    label: version.change_type,
    color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  };

  // Collapse all changes for this version into one readable summary so the row
  // can be a single line. Full detail is still in the `title` tooltip.
  const changeSummaries = version.changes.map((c) => {
    const f = formatChange(c);
    return `${f.label}: ${f.value}`;
  });
  const summary = changeSummaries.join(" · ");

  return (
    <div
      className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted/30 text-xs group"
      title={changeSummaries.join("\n")}
    >
      <Badge
        variant="secondary"
        className={cn("shrink-0 text-[9px] px-1 py-0 h-4", typeConfig.color)}
      >
        {typeConfig.label}
      </Badge>
      <span className="shrink-0 text-muted-foreground text-[10px] tabular-nums">
        {formatRelativeTime(version.timestamp)}
      </span>
      <span className="flex-1 min-w-0 text-foreground truncate">
        {summary || <span className="text-muted-foreground italic">(no change detail)</span>}
      </span>
      <span className="shrink-0 text-muted-foreground text-[10px] flex items-center gap-1">
        <User className="h-2.5 w-2.5" />
        {version.user_name}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={isDeleting}
        className={cn(
          "shrink-0 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity",
          "hover:bg-red-100 dark:hover:bg-red-950 text-muted-foreground hover:text-red-600 dark:hover:text-red-400",
          isDeleting && "opacity-50 cursor-not-allowed"
        )}
        title="Delete this history entry"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

// Synthetic audit row for a weekly snapshot note (regular check-in or closing note).
function NoteItem({
  entry,
}: {
  entry: Extract<AuditEntry, { kind: "weekly_note" }>;
}) {
  const typeConfig = changeTypeLabels[entry.isTerminal ? "closing_note" : "weekly_note"];
  const summary = entry.isTerminal
    ? `Closing note: ${entry.notes}`
    : `Week ${entry.week_number}: ${entry.notes}`;

  return (
    <div
      className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted/30 text-xs"
      title={entry.notes}
    >
      <Badge
        variant="secondary"
        className={cn("shrink-0 text-[9px] px-1 py-0 h-4", typeConfig.color)}
      >
        {typeConfig.label}
      </Badge>
      <span className="shrink-0 text-muted-foreground text-[10px] tabular-nums">
        {formatRelativeTime(entry.timestamp)}
      </span>
      <span className="flex-1 min-w-0 text-foreground truncate">{summary}</span>
      <span className="shrink-0 text-muted-foreground text-[10px] flex items-center gap-1">
        <User className="h-2.5 w-2.5" />
        {entry.user_name}
      </span>
    </div>
  );
}
